import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  EyeOff, 
  Search,
  Filter,
  Palette,
  TrendingUp,
  Users,
  Briefcase,
  Globe,
  Smartphone,
  Zap,
  Server,
  Megaphone,
  Code,
  Database,
  Layers,
  Target,
  Rocket,
  Heart,
  Star,
  Award,
  Shield
} from 'lucide-react';
import { API_URLS } from '../../../config/api';
import { adminApiFetch } from '../../../utils/adminApi';

const AdminProjectCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [availableSubcategories, setAvailableSubcategories] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    icon: 'Briefcase',
    color: 'from-blue-500 to-cyan-500',
    bg_color: 'bg-blue-50',
    count: '0+ Proje',
    trend: '+0%',
    sort_order: 0,
    is_active: true,
    subcategories: [],
    features: [],
    process_steps: []
  });
  const [isSlugManuallyEdited, setIsSlugManuallyEdited] = useState(false);

  const iconOptions = [
    'Briefcase', 'Globe', 'Palette', 'Smartphone', 'Zap', 'Server', 
    'Megaphone', 'Code', 'Database', 'Layers', 'Target', 'Rocket',
    'Heart', 'Star', 'Award', 'Shield', 'Users', 'TrendingUp'
  ];

  const colorOptions = [
    { value: 'from-blue-500 to-cyan-500', label: 'Mavi', bg: 'bg-blue-50' },
    { value: 'from-purple-500 to-pink-500', label: 'Mor', bg: 'bg-purple-50' },
    { value: 'from-orange-500 to-red-500', label: 'Turuncu', bg: 'bg-orange-50' },
    { value: 'from-green-500 to-emerald-500', label: 'Yeşil', bg: 'bg-green-50' },
    { value: 'from-indigo-500 to-purple-500', label: 'İndigo', bg: 'bg-indigo-50' },
    { value: 'from-rose-500 to-pink-500', label: 'Pembe', bg: 'bg-rose-50' },
    { value: 'from-yellow-500 to-orange-500', label: 'Sarı', bg: 'bg-yellow-50' },
    { value: 'from-gray-500 to-gray-700', label: 'Gri', bg: 'bg-gray-50' }
  ];

  useEffect(() => {
    fetchCategories();
    fetchAvailableSubcategories();
  }, []);

  // Slug oluşturma fonksiyonu (Türkçe karakterleri İngilizce'ye çevirir)
  const generateSlug = (text) => {
    if (!text) return '';
    
    return text
      .toLowerCase()
      .replace(/ğ/g, 'g')
      .replace(/ü/g, 'u')
      .replace(/ş/g, 's')
      .replace(/ı/g, 'i')
      .replace(/ö/g, 'o')
      .replace(/ç/g, 'c')
      .replace(/İ/g, 'i')
      .replace(/Ğ/g, 'g')
      .replace(/Ü/g, 'u')
      .replace(/Ş/g, 's')
      .replace(/Ö/g, 'o')
      .replace(/Ç/g, 'c')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  // Kategori adı değiştiğinde slug'ı otomatik oluştur
  const handleNameChange = (name) => {
    setFormData(prev => {
      const newSlug = isSlugManuallyEdited ? prev.slug : generateSlug(name);
      return { ...prev, name, slug: newSlug };
    });
  };

  // Slug manuel olarak değiştirildiğinde flag'i set et
  const handleSlugChange = (slug) => {
    setIsSlugManuallyEdited(true);
    setFormData(prev => ({ ...prev, slug }));
  };

  const fetchAvailableSubcategories = async () => {
    try {
      // Public api.php çağrısı; credential gerekmez, CORS için normal fetch kullan
      const response = await fetch(API_URLS.PROJECT_CATEGORIES);
      const data = await response.json();
      
      // Tüm kategorilerden alt kategorileri topla
      const allSubcategories = [];
      data.forEach(category => {
        if (category.subcategories && Array.isArray(category.subcategories)) {
          category.subcategories.forEach(sub => {
            // Duplicate kontrolü
            if (!allSubcategories.find(existing => existing.name === sub.name)) {
              allSubcategories.push({
                ...sub,
                categoryName: category.name
              });
            }
          });
        }
      });
      
      setAvailableSubcategories(allSubcategories);
    } catch (error) {
      console.error('Error fetching available subcategories:', error);
      setAvailableSubcategories([]);
    }
  };

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await adminApiFetch(API_URLS.ADMIN.PROJECT_CATEGORIES);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        throw new Error(`Non-JSON response: ${text.substring(0, 200)}...`);
      }
      
      const result = await response.json();
      
      if (result.status === 'success') {
        setCategories(result.data || []);
      } else {
        setCategories(result || []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = editingCategory 
        ? `${API_URLS.ADMIN.UPDATE_PROJECT_CATEGORY}&id=${editingCategory.id}`
        : API_URLS.ADMIN.CREATE_PROJECT_CATEGORY;
      
      const method = editingCategory ? 'PUT' : 'POST';
      
      const response = await adminApiFetch(url, {
        method,
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText.substring(0, 200)}`);
      }

      const responseText = await response.text();
      if (!responseText || responseText.trim() === '') {
        throw new Error('Empty response from server');
      }
      
      let result;
      try {
        result = JSON.parse(responseText);
      } catch (parseError) {
        throw new Error(`Invalid JSON response: ${responseText.substring(0, 200)}`);
      }
      
      if (result.status === 'success') {
        await fetchCategories();
        setShowModal(false);
        setEditingCategory(null);
        setFormData({
          name: '',
          slug: '',
          description: '',
          icon: 'Briefcase',
          color: 'from-blue-500 to-cyan-500',
          bg_color: 'bg-blue-50',
          count: '0+ Proje',
          trend: '+0%',
          sort_order: 0,
          is_active: true,
          subcategories: [],
          features: [],
          process_steps: []
        });
        setIsSlugManuallyEdited(false);
      } else {
        alert('Hata: ' + (result.message || 'Bilinmeyen hata'));
      }
    } catch (error) {
      console.error('Error saving category:', error);
      alert('Hata: ' + error.message);
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    
    // Convert features to object format if they are strings
    let features = category.features || [];
    if (features.length > 0 && typeof features[0] === 'string') {
      features = features.map(feature => ({
        name: feature,
        description: `Profesyonel ${feature.toLowerCase()} hizmeti ile projenizi en üst seviyeye taşıyoruz.`
      }));
    }
    
    setFormData({
      name: category.name || '',
      slug: category.slug || generateSlug(category.name || ''),
      description: category.description || '',
      icon: category.icon || 'Briefcase',
      color: category.color || 'from-blue-500 to-cyan-500',
      bg_color: category.bg_color || 'bg-blue-50',
      count: category.count || '0+ Proje',
      trend: category.trend || '+0%',
      sort_order: category.sort_order || 0,
      is_active: category.is_active !== false,
      subcategories: category.subcategories || [],
      features: features,
      process_steps: category.process_steps || []
    });
    setIsSlugManuallyEdited(!!category.slug);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Bu kategoriyi silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      const response = await adminApiFetch(`${API_URLS.ADMIN.DELETE_PROJECT_CATEGORY}?category_id=${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.status === 'success') {
        await fetchCategories();
      } else {
        alert('Hata: ' + (result.message || 'Bilinmeyen hata'));
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      alert('Hata: ' + error.message);
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      const response = await adminApiFetch(`${API_URLS.ADMIN.TOGGLE_PROJECT_CATEGORY_STATUS}?category_id=${id}`, {
        method: 'POST',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        throw new Error(`Non-JSON response: ${text.substring(0, 200)}...`);
      }

      const result = await response.json();
      
      if (result.status === 'success') {
        await fetchCategories();
      } else {
        alert('Hata: ' + (result.message || 'Bilinmeyen hata'));
      }
    } catch (error) {
      console.error('Error toggling status:', error);
      alert('Hata: ' + error.message);
    }
  };

  // Alt kategori yönetimi fonksiyonları
  const addSubcategory = () => {
    setFormData({
      ...formData,
      subcategories: [...formData.subcategories, { name: '', count: '', price: '' }]
    });
  };

  const removeSubcategory = (index) => {
    setFormData({
      ...formData,
      subcategories: formData.subcategories.filter((_, i) => i !== index)
    });
  };

  const updateSubcategory = (index, field, value) => {
    const updatedSubcategories = [...formData.subcategories];
    updatedSubcategories[index] = {
      ...updatedSubcategories[index],
      [field]: value
    };
    setFormData({
      ...formData,
      subcategories: updatedSubcategories
    });
  };

  // Features yönetimi fonksiyonları
  const addFeature = () => {
    setFormData({
      ...formData,
      features: [...formData.features, { name: '', description: '' }]
    });
  };

  const removeFeature = (index) => {
    setFormData({
      ...formData,
      features: formData.features.filter((_, i) => i !== index)
    });
  };

  const updateFeature = (index, field, value) => {
    const updatedFeatures = [...formData.features];
    updatedFeatures[index] = {
      ...updatedFeatures[index],
      [field]: value
    };
    setFormData({
      ...formData,
      features: updatedFeatures
    });
  };

  // Process steps yönetimi fonksiyonları
  const addProcessStep = () => {
    setFormData({
      ...formData,
      process_steps: [...formData.process_steps, { 
        step: formData.process_steps.length + 1,
        title: '', 
        description: '', 
        duration: '', 
        icon: 'Target' 
      }]
    });
  };

  const removeProcessStep = (index) => {
    setFormData({
      ...formData,
      process_steps: formData.process_steps.filter((_, i) => i !== index)
    });
  };

  const updateProcessStep = (index, field, value) => {
    const updatedSteps = [...formData.process_steps];
    updatedSteps[index] = {
      ...updatedSteps[index],
      [field]: value
    };
    setFormData({
      ...formData,
      process_steps: updatedSteps
    });
  };

  const filteredCategories = categories.filter(category =>
    category.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    category.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getIconComponent = (iconName) => {
    const iconMap = {
      Briefcase, Globe, Palette, Smartphone, Zap, Server, 
      Megaphone, Code, Database, Layers, Target, Rocket,
      Heart, Star, Award, Shield, Users, TrendingUp
    };
    return iconMap[iconName] || Briefcase;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Proje Kategorileri</h1>
          <p className="text-gray-600">Proje kategorilerini yönetin</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Yeni Kategori
        </button>
      </div>

      {/* Search */}
      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Kategori ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Categories Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredCategories.map(category => {
          const IconComponent = getIconComponent(category.icon);
          return (
            <div key={category.id} className="bg-white rounded-lg shadow-sm border overflow-hidden">
              <div className={`${category.bg_color} p-6 relative`}>
                <div className="flex items-center justify-between mb-4">
                  <div className={`w-12 h-12 bg-gradient-to-r ${category.color} rounded-lg flex items-center justify-center`}>
                    <IconComponent className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => handleToggleStatus(category.id)}
                      className={`p-2 rounded ${
                        category.is_active 
                          ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {category.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {category.name}
                </h3>
                
                <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                  {category.description}
                </p>
                
                <div className="flex justify-between items-center mb-4">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-medium">{category.count}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <TrendingUp className="w-4 h-4 text-green-600" />
                    <span className="text-sm font-medium text-green-600">{category.trend}</span>
                  </div>
                </div>
                
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(category)}
                    className="flex-1 bg-blue-600 text-white px-3 py-2 rounded text-sm hover:bg-blue-700 flex items-center justify-center gap-1"
                  >
                    <Edit className="w-3 h-3" />
                    Düzenle
                  </button>
                  <button
                    onClick={() => handleDelete(category.id)}
                    className="bg-red-100 text-red-700 px-3 py-2 rounded text-sm hover:bg-red-200 flex items-center justify-center"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filteredCategories.length === 0 && (
        <div className="text-center py-12">
          <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
            <Search className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Kategori bulunamadı</h3>
          <p className="text-gray-600">
            {searchTerm 
              ? 'Arama kriterlerinize uygun kategori bulunamadı.' 
              : 'Henüz hiç kategori eklenmemiş.'}
          </p>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">
                {editingCategory ? 'Kategori Düzenle' : 'Yeni Kategori'}
              </h2>
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditingCategory(null);
                  setFormData({
                    name: '',
                    slug: '',
                    description: '',
                    icon: 'Briefcase',
                    color: 'from-blue-500 to-cyan-500',
                    bg_color: 'bg-blue-50',
                    count: '0+ Proje',
                    trend: '+0%',
                    sort_order: 0,
                    is_active: true,
                    subcategories: [],
                    features: [],
                    process_steps: []
                  });
                  setIsSlugManuallyEdited(false);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Kategori Adı *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => handleNameChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Slug (URL) *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.slug}
                    onChange={(e) => handleSlugChange(e.target.value)}
                    placeholder="kategori-adi"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    URL'de kullanılacak kısa adres. Türkçe karakterler otomatik olarak İngilizce'ye çevrilir.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    İkon
                  </label>
                  <select
                    value={formData.icon}
                    onChange={(e) => setFormData({...formData, icon: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {iconOptions.map(icon => (
                      <option key={icon} value={icon}>{icon}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Açıklama
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Renk
                  </label>
                  <select
                    value={formData.color}
                    onChange={(e) => {
                      const selectedColor = colorOptions.find(c => c.value === e.target.value);
                      setFormData({
                        ...formData, 
                        color: e.target.value,
                        bg_color: selectedColor ? selectedColor.bg : 'bg-blue-50'
                      });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {colorOptions.map(color => (
                      <option key={color.value} value={color.value}>{color.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Proje Sayısı
                  </label>
                  <input
                    type="text"
                    value={formData.count}
                    onChange={(e) => setFormData({...formData, count: e.target.value})}
                    placeholder="25+ Proje"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Büyüme Oranı
                  </label>
                  <input
                    type="text"
                    value={formData.trend}
                    onChange={(e) => setFormData({...formData, trend: e.target.value})}
                    placeholder="+40%"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sıralama
                  </label>
                  <input
                    type="number"
                    value={formData.sort_order}
                    onChange={(e) => setFormData({...formData, sort_order: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">Aktif</span>
              </div>

              {/* Features */}
              <div>
                <div className="flex justify-between items-center mb-3">
                  <label className="block text-sm font-medium text-gray-700">
                    Neler Sunuyoruz? (Özellikler)
                  </label>
                  <button
                    type="button"
                    onClick={addFeature}
                    className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
                  >
                    <Plus className="w-4 h-4" />
                    Özellik Ekle
                  </button>
                </div>
                
                <div className="space-y-3 max-h-60 overflow-y-auto">
                  {formData.features.map((feature, index) => (
                    <div key={index} className="bg-gray-50 p-3 rounded-lg border">
                      <div className="space-y-2">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Özellik Adı
                          </label>
                          <input
                            type="text"
                            value={feature.name || ''}
                            onChange={(e) => updateFeature(index, 'name', e.target.value)}
                            placeholder="Özellik adı (örn: Güvenli Ödeme Sistemleri)"
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Açıklama
                          </label>
                          <textarea
                            value={feature.description || ''}
                            onChange={(e) => updateFeature(index, 'description', e.target.value)}
                            placeholder="Özellik açıklaması (örn: SSL sertifikası ve güvenli ödeme altyapısı ile müşteri bilgilerini koruyoruz)"
                            rows={2}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                        <div className="flex justify-end">
                          <button
                            type="button"
                            onClick={() => removeFeature(index)}
                            className="text-red-600 hover:text-red-800 px-3 py-1 text-xs border border-red-300 rounded hover:bg-red-50 flex items-center gap-1"
                          >
                            <Trash2 className="w-3 h-3" />
                            Sil
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {formData.features.length === 0 && (
                    <div className="text-center py-4 text-gray-500 text-sm">
                      Henüz özellik eklenmemiş. "Özellik Ekle" butonuna tıklayarak kategori özelliklerini ekleyebilirsiniz.
                    </div>
                  )}
                </div>
              </div>

              {/* Alt Kategoriler */}
              <div>
                <div className="flex justify-between items-center mb-3">
                  <label className="block text-sm font-medium text-gray-700">
                    Alt Kategoriler
                  </label>
                  <button
                    type="button"
                    onClick={addSubcategory}
                    className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
                  >
                    <Plus className="w-4 h-4" />
                    Alt Kategori Ekle
                  </button>
                </div>
                
                <div className="space-y-3 max-h-60 overflow-y-auto">
                  {formData.subcategories.map((subcategory, index) => (
                    <div key={index} className="bg-gray-50 p-3 rounded-lg border">
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Alt Kategori Seç
                          </label>
                          <select
                            value={subcategory.name}
                            onChange={(e) => {
                              const selectedSub = availableSubcategories.find(sub => sub.name === e.target.value);
                              if (selectedSub) {
                                updateSubcategory(index, 'name', selectedSub.name);
                                updateSubcategory(index, 'count', selectedSub.count);
                                updateSubcategory(index, 'price', selectedSub.price);
                              }
                            }}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                          >
                            <option value="">Alt kategori seçin</option>
                            {availableSubcategories.map((sub, idx) => (
                              <option key={idx} value={sub.name}>
                                {sub.name} ({sub.categoryName})
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Proje Sayısı
                          </label>
                          <input
                            type="text"
                            value={subcategory.count}
                            onChange={(e) => updateSubcategory(index, 'count', e.target.value)}
                            placeholder="8 Proje"
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Fiyat Aralığı
                          </label>
                          <input
                            type="text"
                            value={subcategory.price}
                            onChange={(e) => updateSubcategory(index, 'price', e.target.value)}
                            placeholder="₺15.000-50.000"
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                        <div className="flex items-end">
                          <button
                            type="button"
                            onClick={() => removeSubcategory(index)}
                            className="text-red-600 hover:text-red-800 text-xs flex items-center gap-1 px-3 py-1 border border-red-300 rounded hover:bg-red-50"
                          >
                            <Trash2 className="w-3 h-3" />
                            Sil
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {formData.subcategories.length === 0 && (
                    <div className="text-center py-4 text-gray-500 text-sm">
                      Henüz alt kategori eklenmemiş. "Alt Kategori Ekle" butonuna tıklayarak mevcut alt kategorilerden seçim yapabilirsiniz.
                    </div>
                  )}
                </div>
              </div>

              {/* Proje Süreci - Alt Kategorilerde Yönetilir */}
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <h3 className="text-sm font-medium text-blue-900">Proje Süreci Yönetimi</h3>
                </div>
                <p className="text-sm text-blue-700 mb-3">
                  Proje süreci adımları artık alt kategori seviyesinde yönetilmektedir.
                </p>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-blue-600">Alt kategorileri düzenlemek için:</span>
                  <a 
                    href="/admin/subcategories" 
                    className="text-sm font-medium text-blue-600 hover:text-blue-800 underline"
                  >
                    Alt Kategoriler sayfasına git →
                  </a>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingCategory(null);
                    setFormData({
                      name: '',
                      slug: '',
                      description: '',
                      icon: 'Briefcase',
                      color: 'from-blue-500 to-cyan-500',
                      bg_color: 'bg-blue-50',
                      count: '0+ Proje',
                      trend: '+0%',
                      sort_order: 0,
                      is_active: true,
                      subcategories: [],
                      features: [],
                      process_steps: []
                    });
                    setIsSlugManuallyEdited(false);
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {editingCategory ? 'Güncelle' : 'Oluştur'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProjectCategories;
